import { getAuthPort } from '@/data/auth';
import { PRODUTO_PADRAO } from '@/data/config';
import { CompilacoesHttpError } from '@/data/http';
import type {
  CompilacoesDataSource,
  EdicaoIncidente,
  EventoIncidente,
  IncidenteDetalhe,
  IncidenteResumo,
  SeveridadeIncidente,
  StatusIncidente,
} from '@/data/types';
import { SEVERIDADES, STATUS_INCIDENTE } from '@/data/types';
import { diaCivilSP } from '@/lib/periodo';
import { CAPABILITY_MAINTAINER, temCapability } from '@/lib/capabilities';
import {
  naoResolvido,
  ordenarParaVersao,
  podeApagarIncidente,
  severidadeMaxima,
  validarCorrigidoEm,
  validarLink,
  validarTitulo,
} from '@/lib/incidentes';
import type { EstadoDemo, IncidenteDemo } from './fixtures';

/**
 * Incidentes em memória (modo demo) — as mesmas regras da API, com o mesmo
 * formato de erro `{"detail": ...}`:
 *
 *  - ler: qualquer um; escrever: `maintainer` ou `admin` (`sysadmin` passa); apagar: só `sysadmin` (403);
 *  - 422 para valor inválido ou `versoes` vazio; 404 com a lista das versões inexistentes;
 *  - PATCH sem mudança real = 409 "nada a alterar"; `link`/`corrigido_em` aceitam `null`;
 *  - resolver grava `resolvido_em/por`, reabrir limpa; edição + status = dois eventos;
 *  - associar versão é idempotente (sem evento); remover a última = 409; versão não associada = 404;
 *  - cursor inválido = 400; filtro de status/severidade inválido = 422.
 */

const USUARIO_DEMO = 'demo@nasajon.com.br';
const LIMITE_PADRAO = 30;

export interface ContextoIncidentes {
  estado: () => EstadoDemo;
  simular: <T>(signal: AbortSignal | undefined, gerar: () => T) => Promise<T>;
}

function erro(recurso: string, status: number, detail: string): CompilacoesHttpError {
  return new CompilacoesHttpError(recurso, status, JSON.stringify({ detail }));
}

/* --------------------------- Agregados por versão --------------------------- */

export function agregadoDaVersao(
  estado: Pick<EstadoDemo, 'incidentes'>,
  versao: string,
  produto: string,
): { incidentes_abertos: number; incidente_severidade_max: SeveridadeIncidente | null } {
  const abertos = estado.incidentes.filter(
    (item) => item.produto === produto && naoResolvido(item) && item.versoes.some((v) => v.versao === versao),
  );
  return { incidentes_abertos: abertos.length, incidente_severidade_max: severidadeMaxima(abertos) };
}

/* -------------------------------- Projeções -------------------------------- */

function resumoDe(item: IncidenteDemo): IncidenteResumo {
  const { descricao, versoes, eventos, ...resto } = item;
  void descricao;
  void eventos;
  return { ...resto, total_versoes: versoes.length };
}

function detalheDe(estado: EstadoDemo, item: IncidenteDemo): IncidenteDetalhe {
  const versoes = item.versoes
    .map((associada) => {
      const build = estado.builds.find((b) => b.versao === associada.versao && b.produto === item.produto);
      return build ? { associada, build } : null;
    })
    .filter((par): par is NonNullable<typeof par> => par !== null)
    .sort((a, b) => b.build.publicado_em.localeCompare(a.build.publicado_em))
    .map(({ associada, build }) => ({
      versao: build.versao,
      branch: build.branch,
      ramo: build.ramo,
      status: build.status,
      adicionado_por: associada.adicionado_por,
      adicionado_em: associada.adicionado_em,
    }));
  return {
    ...resumoDe(item),
    descricao: item.descricao,
    versoes,
    eventos: [...item.eventos].sort((a, b) => b.criado_em.localeCompare(a.criado_em) || b.id - a.id),
  };
}

/* -------------------------------- Validações -------------------------------- */

function exigirEscrita(recurso: string, roles: string[]): void {
  if (!temCapability(roles, CAPABILITY_MAINTAINER)) {
    throw erro(recurso, 403, 'Registrar ou alterar incidentes exige a capability "maintainer" (ou "admin").');
  }
}

function limparTexto(valor: unknown): string | null {
  return typeof valor === 'string' && valor.trim() !== '' ? valor.trim() : null;
}

function validarSeveridade(recurso: string, valor: unknown): SeveridadeIncidente {
  if (!SEVERIDADES.includes(valor as SeveridadeIncidente)) {
    throw erro(recurso, 422, `Severidade inválida. Use: ${SEVERIDADES.join(', ')}.`);
  }
  return valor as SeveridadeIncidente;
}

function validarStatus(recurso: string, valor: unknown): StatusIncidente {
  if (!STATUS_INCIDENTE.includes(valor as StatusIncidente)) {
    throw erro(recurso, 422, `Status inválido. Use: ${STATUS_INCIDENTE.join(', ')}.`);
  }
  return valor as StatusIncidente;
}

function validarLinkOuFalhar(recurso: string, valor: unknown): string | null {
  const texto = limparTexto(valor);
  if (texto === null) return null;
  const problema = validarLink(texto);
  if (problema) throw erro(recurso, 422, `Link inválido: só URLs http ou https são aceitas.`);
  return texto;
}

function validarCorrigidoOuFalhar(recurso: string, valor: unknown): string | null {
  const texto = limparTexto(valor);
  if (texto === null) return null;
  if (validarCorrigidoEm(texto)) {
    throw erro(recurso, 422, 'corrigido_em inválido: use de 2 a 4 números separados por ponto (ex.: 2.2602.80.0).');
  }
  return texto;
}

function unicas(versoes: unknown): string[] {
  if (!Array.isArray(versoes)) return [];
  return [...new Set(versoes.filter((v): v is string => typeof v === 'string').map((v) => v.trim()).filter(Boolean))];
}

export function criarIncidentesDemo({
  estado,
  simular,
}: ContextoIncidentes): Pick<
  CompilacoesDataSource,
  | 'listarIncidentes'
  | 'obterIncidente'
  | 'listarIncidentesDaVersao'
  | 'criarIncidente'
  | 'editarIncidente'
  | 'adicionarVersoesAoIncidente'
  | 'removerVersaoDoIncidente'
  | 'apagarIncidente'
> {
  function acharIncidente(id: number, recurso: string): IncidenteDemo {
    const item = estado().incidentes.find((candidato) => candidato.id === id);
    if (!item) throw erro(recurso, 404, `Incidente ${id} não encontrado.`);
    return item;
  }

  function evento(item: IncidenteDemo, tipo: EventoIncidente['tipo'], detalhe: Record<string, unknown>): void {
    const e = estado();
    e.proximoEventoIncidenteId += 1;
    item.eventos.push({
      id: e.proximoEventoIncidenteId,
      tipo,
      detalhe,
      usuario: USUARIO_DEMO,
      criado_em: new Date().toISOString(),
    });
  }

  /** 404 lista as inexistentes no produto; 422 se existem só em outro produto. */
  function conferirVersoes(recurso: string, versoes: string[], produto: string): void {
    const todas = estado().builds;
    const naoExistem = versoes.filter((versao) => !todas.some((b) => b.versao === versao && b.produto === produto));
    if (naoExistem.length === 0) return;
    const emOutroProduto = naoExistem.filter((versao) => todas.some((b) => b.versao === versao));
    if (emOutroProduto.length > 0) {
      throw erro(recurso, 422, `A versão ${emOutroProduto.join(', ')} pertence a outro produto, não a ${produto}.`);
    }
    throw erro(
      recurso,
      404,
      `${naoExistem.length === 1 ? 'Versão não encontrada' : 'Versões não encontradas'} no produto ${produto}: ${naoExistem.join(', ')}.`,
    );
  }

  return {
    listarIncidentes(filtros, cursor, signal) {
      return simular(signal, () => {
        const recurso = 'os incidentes';
        const status = filtros.status ?? [];
        const severidade = filtros.severidade ?? [];
        status.forEach((valor) => validarStatus(recurso, valor));
        severidade.forEach((valor) => validarSeveridade(recurso, valor));
        let inicio = 0;
        if (cursor) {
          inicio = Number(cursor);
          if (!Number.isInteger(inicio) || inicio < 0) throw erro(recurso, 400, 'Cursor inválido.');
        }
        const termo = filtros.q?.trim().toLowerCase() ?? '';
        const versao = filtros.versao?.trim() ?? '';
        const filtrados = estado()
          .incidentes.filter((item) => !filtros.produto || item.produto === filtros.produto)
          .filter((item) => status.length === 0 || status.includes(item.status))
          .filter((item) => severidade.length === 0 || severidade.includes(item.severidade))
          .filter((item) => !versao || item.versoes.some((v) => v.versao === versao))
          .filter((item) => !termo || item.titulo.toLowerCase().includes(termo))
          .filter((item) => !filtros.criado_de || diaCivilSP(item.criado_em) >= filtros.criado_de)
          .filter((item) => !filtros.criado_ate || diaCivilSP(item.criado_em) <= filtros.criado_ate)
          .sort((a, b) => b.criado_em.localeCompare(a.criado_em) || b.id - a.id);
        const limite = Math.min(Math.max(filtros.limit ?? LIMITE_PADRAO, 1), 100);
        const pagina = filtrados.slice(inicio, inicio + limite);
        const fim = inicio + pagina.length;
        return { items: pagina.map(resumoDe), next_cursor: fim < filtrados.length ? String(fim) : null };
      });
    },

    obterIncidente(id, signal) {
      return simular(signal, () => detalheDe(estado(), acharIncidente(id, `o incidente #${id}`)));
    },

    listarIncidentesDaVersao(versao, produto = PRODUTO_PADRAO, signal) {
      return simular(signal, () => {
        const e = estado();
        if (!e.builds.some((b) => b.versao === versao && b.produto === produto)) {
          throw erro(`os incidentes da versão ${versao}`, 404, `Versão ${versao} não encontrada.`);
        }
        const afetam = e.incidentes.filter(
          (item) => item.produto === produto && item.versoes.some((v) => v.versao === versao),
        );
        return ordenarParaVersao(afetam.map(resumoDe));
      });
    },

    async criarIncidente(dados) {
      const roles = await getAuthPort().getRoles();
      return simular(undefined, () => {
        const recurso = 'o registro do incidente';
        exigirEscrita(recurso, roles);
        const titulo = typeof dados.titulo === 'string' ? dados.titulo.trim() : '';
        const problemaTitulo = validarTitulo(titulo);
        if (problemaTitulo) throw erro(recurso, 422, problemaTitulo);
        const severidade = validarSeveridade(recurso, dados.severidade);
        const link = validarLinkOuFalhar(recurso, dados.link);
        const corrigido_em = validarCorrigidoOuFalhar(recurso, dados.corrigido_em);
        const versoes = unicas(dados.versoes);
        if (versoes.length === 0) throw erro(recurso, 422, 'Informe pelo menos uma versão afetada.');
        const produto = dados.produto ?? PRODUTO_PADRAO;
        conferirVersoes(recurso, versoes, produto);

        const e = estado();
        const agora = new Date().toISOString();
        const incidente: IncidenteDemo = {
          id: e.proximoIncidenteId++,
          produto,
          titulo,
          descricao: limparTexto(dados.descricao) ?? '',
          severidade,
          status: 'aberto',
          link,
          corrigido_em,
          criado_por: USUARIO_DEMO,
          criado_em: agora,
          atualizado_em: agora,
          resolvido_em: null,
          resolvido_por: null,
          versoes: versoes.map((versao) => ({ versao, adicionado_por: USUARIO_DEMO, adicionado_em: agora })),
          eventos: [],
        };
        e.incidentes.push(incidente);
        evento(incidente, 'criado', { titulo, severidade, link, corrigido_em, versoes });
        return detalheDe(e, incidente);
      });
    },

    async editarIncidente(id, edicao: EdicaoIncidente) {
      const roles = await getAuthPort().getRoles();
      return simular(undefined, () => {
        const recurso = `a edição do incidente #${id}`;
        exigirEscrita(recurso, roles);
        const incidente = acharIncidente(id, recurso);
        const corpo = edicao as Record<string, unknown>;
        const campos: Record<string, { de: unknown; para: unknown }> = {};
        let novoStatus: StatusIncidente | null = null;

        if ('titulo' in corpo) {
          const texto = typeof corpo.titulo === 'string' ? corpo.titulo.trim() : '';
          const problema = validarTitulo(texto);
          if (problema) throw erro(recurso, 422, problema);
          if (texto !== incidente.titulo) campos.titulo = { de: incidente.titulo, para: texto };
        }
        if ('descricao' in corpo) {
          if (typeof corpo.descricao !== 'string') throw erro(recurso, 422, 'A descrição não pode ser nula.');
          const texto = corpo.descricao.trim();
          if (texto !== incidente.descricao) campos.descricao = { de: incidente.descricao, para: texto };
        }
        if ('severidade' in corpo) {
          const severidade = validarSeveridade(recurso, corpo.severidade);
          if (severidade !== incidente.severidade) campos.severidade = { de: incidente.severidade, para: severidade };
        }
        if ('link' in corpo) {
          const link = validarLinkOuFalhar(recurso, corpo.link);
          if (link !== incidente.link) campos.link = { de: incidente.link, para: link };
        }
        if ('corrigido_em' in corpo) {
          const corrigido = validarCorrigidoOuFalhar(recurso, corpo.corrigido_em);
          if (corrigido !== incidente.corrigido_em) campos.corrigido_em = { de: incidente.corrigido_em, para: corrigido };
        }
        if ('status' in corpo) {
          const status = validarStatus(recurso, corpo.status);
          if (status !== incidente.status) novoStatus = status;
        }

        if (Object.keys(campos).length === 0 && novoStatus === null) {
          throw erro(recurso, 409, 'Nada a alterar: os valores enviados já são os atuais.');
        }

        const agora = new Date().toISOString();
        if (Object.keys(campos).length > 0) {
          for (const [campo, mudanca] of Object.entries(campos)) {
            (incidente as unknown as Record<string, unknown>)[campo] = mudanca.para;
          }
          evento(incidente, 'editado', campos);
        }
        if (novoStatus !== null) {
          const anterior = incidente.status;
          incidente.status = novoStatus;
          if (novoStatus === 'resolvido') {
            incidente.resolvido_em = agora;
            incidente.resolvido_por = USUARIO_DEMO;
          } else {
            incidente.resolvido_em = null;
            incidente.resolvido_por = null;
          }
          evento(incidente, 'status', { de: anterior, para: novoStatus });
        }
        incidente.atualizado_em = agora;
        return detalheDe(estado(), incidente);
      });
    },

    async adicionarVersoesAoIncidente(id, versoes) {
      const roles = await getAuthPort().getRoles();
      return simular(undefined, () => {
        const recurso = `a associação de versões ao incidente #${id}`;
        exigirEscrita(recurso, roles);
        const incidente = acharIncidente(id, recurso);
        const lista = unicas(versoes);
        if (lista.length === 0) throw erro(recurso, 422, 'Informe pelo menos uma versão.');
        conferirVersoes(recurso, lista, incidente.produto);
        const agora = new Date().toISOString();
        for (const versao of lista) {
          if (incidente.versoes.some((v) => v.versao === versao)) continue; // idempotente: sem evento
          incidente.versoes.push({ versao, adicionado_por: USUARIO_DEMO, adicionado_em: agora });
          evento(incidente, 'versao_adicionada', { versao });
          incidente.atualizado_em = agora;
        }
        return detalheDe(estado(), incidente);
      });
    },

    async removerVersaoDoIncidente(id, versao) {
      const roles = await getAuthPort().getRoles();
      return simular(undefined, () => {
        const recurso = `a remoção da versão ${versao} do incidente #${id}`;
        exigirEscrita(recurso, roles);
        const incidente = acharIncidente(id, recurso);
        if (!incidente.versoes.some((v) => v.versao === versao)) {
          throw erro(recurso, 404, `A versão ${versao} não está associada ao incidente #${id}.`);
        }
        if (incidente.versoes.length === 1) {
          throw erro(recurso, 409, 'Não é possível remover a última versão do incidente. Apague o incidente, se for o caso.');
        }
        incidente.versoes = incidente.versoes.filter((v) => v.versao !== versao);
        evento(incidente, 'versao_removida', { versao });
        incidente.atualizado_em = new Date().toISOString();
        return detalheDe(estado(), incidente);
      });
    },

    async apagarIncidente(id) {
      const roles = await getAuthPort().getRoles();
      return simular(undefined, () => {
        const recurso = `a exclusão do incidente #${id}`;
        const acesso = podeApagarIncidente(roles);
        if (!acesso.permitido) throw erro(recurso, 403, acesso.motivo);
        const incidente = acharIncidente(id, recurso);
        const e = estado();
        e.incidentes = e.incidentes.filter((item) => item !== incidente);
      });
    },
  };
}
